import { DatasetEpisodeWarningBadge } from "@/components/dataset/DatasetEpisodeWarningBadge";
import { datasetStorageBadge } from "@/components/dataset/DatasetStorageBadge";
import { LabelChip, Text, TruncatedText, type DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { DatasetLabel } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { DatasetPreview, formatDatasetLabel } from "@/components/dataset";
import { HatLabelChip } from "@/components/label";
import { MemoPopover } from "@/components/memo";
import { HatTooltip } from "@/components/tooltip";
import { formatBytes, formatNumber } from "@/utils";
import { formatDateTime, formatDuration, parseUtcMs } from "@/utils/datetime";
import type { DatasetTableRow } from "./DatasetTableRow";
import styles from "./DatasetPage.module.css";

// 이름 칸의 글자. 칸이 좁으면 말줄임하고 툴팁으로 전체 이름을 보인다. c6 는 Text variant 의 `as` 로
// TruncatedText 를 넣는다. 이름이 줄어들지 않으면 옆의 저장 위치 칩 아래로 넘친다.
const truncatedName = { ...hatcheryTextModes["body-bold"], as: TruncatedText };

// 정체성 facet 셀. 1줄은 embodiment 이름, 2줄은 팔 구성 · EE(값이 있는 축만)다. 세 축 모두 서버
// 정렬이 없어 한 컬럼에 세로로 쌓는다. 행이 2줄 높이까지 늘어나는 것을 허용한다.
function identityCell(item: DatasetTableRow): ReactNode {
  const detail = [item.armsLabel, item.endEffectorName].filter((part) => part !== null).join(" · ");
  return (
    <div className={styles.identityCell}>
      <Text variant={hatcheryTextModes["caption-normal"]}>{item.embodimentName}</Text>
      {detail === "" ? null : (
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.identityMeta}>
          {detail}
        </Text>
      )}
    </div>
  );
}

export type DatasetColumnOptions = {
  // 대표 섬네일 presigned URL 이 만료되면(onError) 목록을 디바운스해 재조회한다. 없으면 이니셜 폴백만 쓴다.
  onThumbnailError?: () => void;
  onPendingCandidatesClick?: (row: DatasetTableRow) => void;
  // 채널 누락 건수를 누르면 부른다. 없으면 건수를 글자로만 표시한다.
  onMissingChannelClick?: (row: DatasetTableRow) => void;
};

// Dataset 목록 테이블 컬럼. 식별(섬네일, 이름) → 정체성 facet → 지표 → 라벨 → 운영자 순이다.
// 카드 뷰와 같이 대표 섬네일을 표시하고, 만료되면 부모가 주입한 onThumbnailError 로 재조회한다.
//
// `sortable` 은 서버가 정렬을 지원하는 열에만 둔다(`DatasetCriteriaTableSource` 의 컬럼 → 정렬 기준
// 매핑과 맞춘다). 정체성 facet, 라벨, 담당자, 메모는 서버 정렬 기준이 없어 헤더를 정렬 가능하게 하지
// 않는다. 정렬 가능하게 하면 로드된 페이지만 정렬되어 페이지 2 에서 "전체 정렬" 처럼 보이는 잘못된
// 순서가 나온다.
export function buildDatasetColumns({
  onThumbnailError,
  onPendingCandidatesClick,
  onMissingChannelClick,
}: DatasetColumnOptions = {}): readonly DataTableColumn<DatasetTableRow>[] {
  return [
    {
      key: "display_name",
      header: "이름",
      width: "minmax(360px, 1fr)",
      cellPadding: "tight",
      sortable: "both",
      render: (item) => (
        <div className={styles.nameCell}>
          <DatasetPreview
            label={item.display_name}
            imageUrl={item.thumbnail_url}
            mode="table"
            onImageError={onThumbnailError}
          />
          <div className={styles.nameLine}>
            <div className={styles.nameText}>
              <Text variant={truncatedName}>{item.display_name}</Text>
            </div>
            {datasetStorageBadge(item.storage_backend)}
          </div>
        </div>
      ),
    },
    {
      key: "quality_candidate_pending_count",
      header: "미처리 품질 후보",
      width: "230px",
      render: (item) =>
        item.quality_candidate_pending_count === undefined ? (
          "—"
        ) : item.quality_candidate_pending_count === 0 ? (
          "없음"
        ) : onPendingCandidatesClick ? (
          <DatasetEpisodeWarningBadge
            kind="qualityCandidate"
            count={item.quality_candidate_pending_count}
            onOpen={() => onPendingCandidatesClick(item)}
          />
        ) : (
          `에피소드 ${item.quality_candidate_pending_count.toLocaleString()}개`
        ),
    },
    {
      key: "missing_channel_episode_count",
      header: "채널 누락",
      width: "200px",
      // 채널 파일이 빠진 에피소드 수다. 업로드는 이 에피소드를 제외해 둔다. 복원한 에피소드도 센다.
      render: (item) =>
        item.missing_channel_episode_count === 0 ? (
          "없음"
        ) : onMissingChannelClick ? (
          <DatasetEpisodeWarningBadge
            kind="missingChannel"
            count={item.missing_channel_episode_count}
            onOpen={() => onMissingChannelClick(item)}
          />
        ) : (
          `에피소드 ${item.missing_channel_episode_count.toLocaleString()}개`
        ),
    },
    {
      key: "embodimentName",
      header: "Embodiment",
      width: "180px",
      render: identityCell,
    },
    {
      key: "total_episodes",
      header: "에피소드 수",
      width: "100px",
      align: "right",
      sortable: "both",
      format: (value) => formatNumber(Number(value)),
    },
    {
      // 에피소드 길이의 중앙값이다. 총 분량이 아니다. 길이 필터(`duration_min`/`duration_max`)는
      // 총 분량을 보므로 두 값이 다르다. 헤더가 그 차이를 보여 줘야 한다.
      key: "median_episode_length_sec",
      header: "길이 중앙값",
      width: "110px",
      align: "right",
      sortable: "both",
      render: (item) =>
        formatDuration(
          item.median_episode_length_sec === null ? null : item.median_episode_length_sec * 1000,
        ),
    },
    {
      // 에피소드 길이의 합이다. 중앙값 열 옆에 두지만 폭 예산 때문에 기본은 숨김이다.
      // 두 값을 비교하려는 사용자가 컬럼 메뉴에서 켠다.
      key: "total_duration_sec",
      header: "총 길이",
      width: "110px",
      align: "right",
      visible: false,
      sortable: "both",
      render: (item) =>
        formatDuration(item.total_duration_sec === null ? null : item.total_duration_sec * 1000),
    },
    {
      key: "total_size_bytes",
      header: "용량",
      width: "96px",
      align: "right",
      sortable: "both",
      render: (item) => formatBytes(item.total_size_bytes, "—"),
    },
    {
      key: "labels",
      header: "라벨",
      width: "220px",
      className: styles.labelsCell,
      render: (item) => renderDatasetLabels(item.labels),
    },
    {
      // 사람이 정리한 위치. 좌측 트리로 좁힌 동안에는 중복 정보다. 하지만 검색만 했거나 미분류를
      // 보는 동안에는 이 열만 위치를 알려 준다. 서버 정렬 기준이 없어 헤더를 정렬 가능하게
      // 하지 않는다.
      key: "folderPath",
      header: "폴더",
      width: "180px",
      render: (item) => (
        <Text
          variant={hatcheryTextModes["caption-normal"]}
          className={item.folderPath === null ? styles.folderUnfiled : styles.folderPath}
        >
          {item.folderPath ?? "미분류"}
        </Text>
      ),
    },
    {
      key: "operator_member",
      header: "담당자",
      width: "130px",
      render: (item) => (
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.operatorName}>
          {item.operator_member?.display_name ?? "-"}
        </Text>
      ),
    },
    {
      // 메모(댓글) 표시기 + 앵커 팝업. row.uuid 로 대상을 만든다.
      key: "memo_count",
      header: "메모",
      width: "72px",
      align: "center",
      render: (item) => (
        <MemoPopover datasetUuid={item.uuid} count={item.memo_count} latest={item.latest_memo} />
      ),
    },
    {
      // 목록 기본 순서를 정하는 값이라 열을 둔다. 폭 예산 때문에 기본은 숨김이다. 컬럼
      // 메뉴로 켜면 생성 시각을 보며 오름/내림을 고를 수 있다.
      key: "created_at",
      header: "생성일",
      width: "150px",
      visible: false,
      sortable: "both",
      render: (item) => (
        <Text variant={hatcheryTextModes["caption-normal"]}>
          {formatDateTime(parseUtcMs(item.created_at))}
        </Text>
      ),
    },
    {
      key: "artifact_source_uuid",
      header: "Artifact 소스",
      width: "260px",
      visible: false,
      render: (item) => <span className={styles.sourceUuid}>{item.artifact_source_uuid}</span>,
    },
  ];
}

function renderDatasetLabels(labels: readonly DatasetLabel[]): ReactNode {
  const isCollapsed = labels.length >= 3;
  const visibleLabels = isCollapsed ? labels.slice(0, 1) : labels;
  const hiddenCount = labels.length - visibleLabels.length;

  return (
    <HatTooltip
      content={isCollapsed ? renderDatasetLabelTooltip(labels) : null}
      disabled={!isCollapsed}
      placement="bottom"
      mode="plain"
    >
      <span className={styles.labelList}>
        {labels.length === 0 ? (
          <Text variant={hatcheryTextModes["caption-normal"]} className={styles.labelEmpty}>
            -
          </Text>
        ) : (
          <>
            {visibleLabels.map((label, index) =>
              // collapsed 면 바깥 overflow 툴팁이 표시되어 칩별 가이드 툴팁과 겹친다. 그래서 일반 칩으로 둔다.
              isCollapsed ? (
                <LabelChip
                  key={`${formatDatasetLabel(label)}-${index}`}
                  className={styles.labelItem}
                  color={label.color}
                  size="xs"
                  variant="filled"
                  title={formatDatasetLabel(label)}
                >
                  {formatDatasetLabel(label)}
                </LabelChip>
              ) : (
                <HatLabelChip
                  key={`${formatDatasetLabel(label)}-${index}`}
                  className={styles.labelItem}
                  labelKey={label.key}
                  value={label.value}
                  color={label.color}
                  description={label.description}
                  size="xs"
                >
                  {formatDatasetLabel(label)}
                </HatLabelChip>
              ),
            )}
            {isCollapsed ? (
              <Text
                variant={hatcheryTextModes["caption-bold"]}
                className={styles.labelSummaryCount}
              >
                외 {hiddenCount}개
              </Text>
            ) : null}
          </>
        )}
      </span>
    </HatTooltip>
  );
}

function renderDatasetLabelTooltip(labels: readonly DatasetLabel[]): ReactNode {
  return (
    <span className={styles.labelTooltipList}>
      {labels.map((label, index) => (
        <LabelChip
          key={`${formatDatasetLabel(label)}-${index}`}
          className={styles.labelTooltipItem}
          color={label.color}
          size="xs"
          variant="filled"
          title={formatDatasetLabel(label)}
        >
          {formatDatasetLabel(label)}
        </LabelChip>
      ))}
    </span>
  );
}
